import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type ToolbarFilterSelectProps<T extends string = string> = {
  id: string;
  label: string;
  value: T;
  onChange: (value: T) => void;
  /** Value that represents "all" / no filter. Defaults to empty string. */
  allValue?: T;
  renderSelected?: (value: T) => ReactNode;
  children: ReactNode;
};

/**
 * Outlined Select for toolbar filters. Label stays shrunk so "All" does not overlap the label.
 */
const ToolbarFilterSelect = <T extends string = string>({
  id,
  label,
  value,
  onChange,
  allValue = '' as T,
  renderSelected,
  children,
}: ToolbarFilterSelectProps<T>) => {
  const { t } = useTranslation();

  return (
    <FormControl size="small" fullWidth>
      <InputLabel id={id} shrink>
        {label}
      </InputLabel>
      <Select
        labelId={id}
        value={value}
        label={label}
        onChange={(e: SelectChangeEvent<T>) => onChange(e.target.value as T)}
        displayEmpty={allValue === ('' as T)}
        renderValue={selected => {
          if (selected === allValue) {
            return t('base.all');
          }
          return renderSelected?.(selected as T) ?? selected;
        }}
      >
        {children}
      </Select>
    </FormControl>
  );
};

export default ToolbarFilterSelect;
